import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";

// Must match backend constants/appointment.js exactly.
const COMPLETE_DISPOSITIONS = [
  "Consultation completed",
  "Follow-up scheduled",
  "Referred to specialist",
  "Admitted",
  "Other",
];

const CANCEL_DISPOSITIONS = [
  "Patient requested cancellation",
  "Doctor unavailable",
  "No show",
  "Duplicate entry",
  "Other",
];

const CONFIG = {
  complete: {
    title: "Complete appointment",
    label: "Disposition",
    placeholder: "Select an outcome for this visit…",
    options: COMPLETE_DISPOSITIONS,
    confirmLabel: "Mark complete",
  },
  cancel: {
    title: "Cancel appointment",
    label: "Reason for cancellation",
    placeholder: "Select a reason…",
    options: CANCEL_DISPOSITIONS,
    confirmLabel: "Cancel appointment",
  },
};

export default function DispositionModal({ open, onOpenChange, appointment, action, onConfirm }) {
  const [disposition, setDisposition] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setDisposition("");
      setNote("");
    }
  }, [open, appointment]);

  if (!appointment || !action) return null;
  const config = CONFIG[action];
  const trimmedNote = note.trim();
  // Cancelling always needs a note — grays out the button instead of
  // letting the click through to a toast error, same as everywhere else
  // required fields are enforced in this app.
  const canSubmit = action === "cancel" ? !!trimmedNote : !!disposition || !!trimmedNote;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      await onConfirm(appointment, { disposition: disposition || undefined, note: trimmedNote || undefined });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {config.title} #{appointment._id.slice(-6)}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>{config.label}</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={disposition}
              onChange={(e) => setDisposition(e.target.value)}
            >
              <option value="">{config.placeholder}</option>
              {config.options.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label>{action === "cancel" ? "Note *" : "Note"}</Label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add a note…"
              rows={3}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>

          <p className="text-xs text-muted-foreground">
            {action === "cancel"
              ? "A note is required to cancel this appointment."
              : "A reason or a note is required — at least one of the two."}
          </p>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Back
            </Button>
            <Button type="submit" disabled={saving || !canSubmit}>
              {saving ? "Saving…" : config.confirmLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
